import { Alert, Loader, Stack, Text } from '@mantine/core';
import { RunTestOutcome } from '@/types/runTestOutcome.ts';

type Props = {
  outcome?: RunTestOutcome;
  isLoading?: boolean;
  errorMessage?: string | null;
};

const RunTestOutcomePanel = ({ outcome, isLoading = false, errorMessage = null }: Props) => {
  if (isLoading && !outcome) {
    return (
      <Alert color="blue" variant="light" title="Run verification">
        <Stack gap={6}>
          <Text size="sm">Loading run verification</Text>
          <Loader size="xs" />
        </Stack>
      </Alert>
    );
  }

  if (errorMessage && !outcome) {
    return (
      <Alert color="yellow" variant="light" title="Run verification">
        <Text size="sm">Unable to load run verification. {errorMessage}</Text>
      </Alert>
    );
  }

  if (!outcome) return null;

  const testKindMessage =
    outcome.test_kind === 'safe_test' ? 'Safe execution.' : 'Effectful execution.';
  const externalEffectsMessage =
    outcome.external_effects === 'blocked'
      ? 'External actions were blocked.'
      : 'External actions may have occurred.';
  const expectedFailureVerified = Boolean(outcome.expected_failure);
  let color = 'blue';
  if (errorMessage) {
    color = 'yellow';
  } else if (
    outcome.bounded_test?.status === 'failed' ||
    outcome.verification_status === 'failed' ||
    (outcome.failure && !expectedFailureVerified)
  ) {
    color = 'red';
  } else if (
    outcome.bounded_test?.status === 'incomplete' ||
    outcome.verification_status === 'incomplete'
  ) {
    color = 'yellow';
  } else if (
    outcome.bounded_test?.status !== 'pending' &&
    (outcome.bounded_test?.status === 'passed' || outcome.verification_status === 'passed')
  ) {
    color = 'green';
  }
  return (
    <Alert
      color={color}
      variant="light"
      title={
        outcome.test_purpose === 'diagnostic'
          ? 'Diagnostic run verification'
          : outcome.test_purpose === 'regression'
            ? 'Regression run verification'
            : 'Run verification'
      }
    >
      <Stack gap={4}>
        {errorMessage && (
          <Text size="sm">
            Showing the last available verification; refresh failed. {errorMessage}
          </Text>
        )}
        <Text size="sm">{outcome.summary}</Text>
        {outcome.bounded_test && (
          <>
            <Text size="sm" fw={600}>
              Bounded browser test: {outcome.bounded_test.status}
            </Text>
            {outcome.bounded_test.stop_before_stage_id && (
              <Text size="sm">Stop before: {outcome.bounded_test.stop_before_stage_id}</Text>
            )}
            {!!outcome.bounded_test.completed_stage_ids.length && (
              <Text size="sm">
                Verified stages: {outcome.bounded_test.completed_stage_ids.join(', ')}
              </Text>
            )}
            <Text size="sm">
              Cleanup: {outcome.bounded_test.cleanup_confirmed ? 'confirmed' : 'not confirmed'}
            </Text>
          </>
        )}
        {outcome.verification_message && <Text size="sm">{outcome.verification_message}</Text>}
        {outcome.assertion_status && (
          <Text size="sm">
            Expected-result checks: {outcome.assertion_status.replaceAll('_', ' ')}
          </Text>
        )}
        {outcome.execution_integrity_status && (
          <Text size="sm">Execution integrity: {outcome.execution_integrity_status}</Text>
        )}
        {!!outcome.effective_fresh_step_ids?.length && (
          <Text size="sm">Fresh steps: {outcome.effective_fresh_step_ids.join(', ')}</Text>
        )}
        <Text size="sm">{testKindMessage}</Text>
        <Text size="sm">{externalEffectsMessage}</Text>
        {outcome.expected_failure && (
          <Text size="sm">
            Expected safe-test failure verified:{' '}
            <Text span fw={600}>
              {outcome.expected_failure.message}
            </Text>
          </Text>
        )}
        {outcome.simulated_output_used && (
          <Text size="sm">
            Some test output was simulated; no provider result was created for that simulated work.
          </Text>
        )}
        {outcome.failure && (
          <Text size="sm">
            {expectedFailureVerified
              ? 'Observed expected condition: '
              : outcome.failure.certainty === 'confirmed'
                ? 'Confirmed condition: '
                : 'Reported failure: '}
            <Text span fw={600}>
              {outcome.failure.text}
            </Text>
          </Text>
        )}
        {outcome.limitations.map((limitation) => (
          <Text key={limitation} size="sm" c="dimmed">
            {limitation}
          </Text>
        ))}
      </Stack>
    </Alert>
  );
};

export default RunTestOutcomePanel;
